import { useQuery } from '@apollo/client';
import { AnalyticsEvent, SubscriptionApp } from '@focuslee/shared';
import { Stack } from '@mui/material';
import { Button, Caption } from '@telegram-apps/telegram-ui';
import { miniApp, retrieveLaunchParams } from '@tma.js/sdk';
import { type ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BaseSpinner } from '../components/BaseSpinner';
import { ButtonDock } from '../components/ButtonDock';
import { Page } from '../components/Page';
import { isPremiumStartParam } from '../helpers/premiumLink';
import { getPremiumUserQuery, myRenewalMissedQuery } from './operations';
import { PremiumFeatures } from './PremiumFeatures';
import { PremiumHeader } from './PremiumHeader';
import { getPremiumState, PremiumUser } from './premiumState';
import { usePremiumActions } from './usePremiumActions';

export interface SubscriptionPageProps {
    app: SubscriptionApp;
    artwork: ReactNode;
    recordEvent: (event: string, properties: Record<string, string | number>) => void;
    onEntitlementChanged: (user: PremiumUser) => void;
}

export function SubscriptionPage({ app, artwork, recordEvent, onEntitlementChanged }: SubscriptionPageProps) {
    const { t } = useTranslation('subscription');
    const navigate = useNavigate();
    const location = useLocation();
    const startParam = retrieveLaunchParams().tgWebAppStartParam ?? new URLSearchParams(window.location.search).get('startapp');
    const isDeepLinkEntry = isPremiumStartParam(startParam);
    const source = isDeepLinkEntry ? 'deep_link' : (location.state as { source?: string } | null)?.source ?? 'profile';
    const handleBack = useCallback(() => navigate(-1), [navigate]);
    const handleClose = useCallback(() => {
        if (miniApp.close.isAvailable()) miniApp.close();
        else window.close();
    }, []);
    const handleTrialStarted = useCallback(() => {
        if (isDeepLinkEntry) handleClose();
    }, [handleClose, isDeepLinkEntry]);
    const { loading, data, error, refetch } = useQuery<{ user: PremiumUser }>(getPremiumUserQuery, { fetchPolicy: 'network-only' });
    const {
        data: renewalData,
        loading: renewalLoading,
        error: renewalError,
        refetch: refetchRenewal,
    } = useQuery<{ myRenewalMissed: boolean }>(myRenewalMissedQuery, {
        fetchPolicy: 'network-only',
    });
    const actions = usePremiumActions(data?.user, app, source, handleTrialStarted, onEntitlementChanged, recordEvent);
    const userId = data?.user.id;

    useEffect(() => {
        if (!userId) return;
        recordEvent(AnalyticsEvent.paywallViewed, { source });
    }, [userId, source, recordEvent]);

    useEffect(() => {
        if (data?.user) onEntitlementChanged(data.user);
    }, [data?.user, onEntitlementChanged]);

    if (error || renewalError) {
        return (
            <Page edgeToEdge backClick={isDeepLinkEntry ? handleClose : undefined}>
                <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', px: 2 }} spacing={2}>
                    <Caption>{t('loadError')}</Caption>
                    <Button
                        onClick={() => {
                            void Promise.all([refetch(), refetchRenewal()]).catch(console.error);
                        }}
                    >
                        {t('retry')}
                    </Button>
                </Stack>
            </Page>
        );
    }

    if (loading || renewalLoading || !data) {
        return (
            <Page edgeToEdge backClick={isDeepLinkEntry ? handleClose : undefined}>
                <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <BaseSpinner size="m" />
                </Stack>
            </Page>
        );
    }

    const { user } = data;
    const state = getPremiumState(user);
    const ctaText = t(state.shouldStartTrial ? 'startTrial' : 'subscribe');

    return (
        <Page
            edgeToEdge
            backClick={isDeepLinkEntry ? handleClose : undefined}
            footer={
                <ButtonDock
                    main={
                        state.canSubscribe
                            ? {
                                  text: ctaText,
                                  onClick: state.shouldStartTrial ? actions.handleStartTrial : actions.handleSubscribe,
                                  loading: state.shouldStartTrial ? actions.startingTrial : actions.subscribing,
                              }
                            : undefined
                    }
                    secondary={{
                        text: isDeepLinkEntry ? t('close') : t('back'),
                        onClick: isDeepLinkEntry ? handleClose : handleBack,
                        navigation: isDeepLinkEntry ? 'close' : 'back',
                    }}
                />
            }
        >
            <Stack
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 2,
                    pt: 'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px) + 5px)',
                    pb: '96px',
                }}
                spacing={3}
            >
                <PremiumHeader app={app} artwork={artwork} user={user} renewalMissed={renewalData?.myRenewalMissed ?? false} />
                <Caption style={{ color: 'var(--tg-theme-hint-color)', textAlign: 'center' }}>{t('sharedAccess')}</Caption>
                <PremiumFeatures app={app} hasPremium={state.hasPremium} />
                {state.canStartAdminTrial && (
                    <Button
                        mode="plain"
                        size="m"
                        stretched
                        style={{ color: 'var(--tgui--button_color)' }}
                        disabled={actions.startingTrial || actions.subscribing}
                        onClick={actions.handleStartTrial}
                    >
                        {t('startTrial')}
                    </Button>
                )}
                {!state.isAdmin && !state.isManualPremium && (!state.hasUsedTrial || state.isTrialActive) && (
                    <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, textAlign: 'center' }}>{t('trialNote')}</Caption>
                )}
                {state.isSubscriptionActive && user.subscription && !user.subscription.subscriptionCanceledAt && (
                    <Button
                        mode="plain"
                        size="m"
                        stretched
                        style={{ color: 'var(--tgui--button_color)' }}
                        disabled={actions.canceling}
                        onClick={actions.handleCancel}
                    >
                        {t('cancelSubscription')}
                    </Button>
                )}
            </Stack>
        </Page>
    );
}
